/**
 * Client-side avatar processing: resize + optional greyscale + JPEG compress.
 * Keeps Storage / ecommerce payloads small without relying on the server.
 */

const DEFAULT_MAX_EDGE = 400;
const DEFAULT_QUALITY = 0.8;

function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    // Required when source is a Firebase Storage URL drawn onto canvas
    if (typeof src === "string" && src.startsWith("http")) {
      img.crossOrigin = "anonymous";
    }
    img.onload = () => resolve(img);
    img.onerror = () =>
      reject(new Error("Could not load image for processing."));
    img.src = src;
  });
}

function computeTargetSize(width, height, maxEdge) {
  const longest = Math.max(width, height);
  if (longest <= maxEdge) {
    return { width, height };
  }
  const scale = maxEdge / longest;
  return {
    width: Math.max(1, Math.round(width * scale)),
    height: Math.max(1, Math.round(height * scale))
  };
}

/**
 * @param {string} source - data URL or remote image URL
 * @param {object} [opts]
 * @param {number} [opts.maxEdge=400] - longest side in px
 * @param {number} [opts.quality=0.8] - JPEG quality 0–1
 * @param {boolean} [opts.greyscale=false]
 * @returns {Promise<{ dataUrl: string, byteLength: number, width: number, height: number }>}
 */
export async function processAvatarImage(source, opts = {}) {
  if (!source) {
    throw new Error("No image to process.");
  }

  const maxEdge = opts.maxEdge || DEFAULT_MAX_EDGE;
  const quality =
    typeof opts.quality === "number" ? opts.quality : DEFAULT_QUALITY;
  const greyscale = !!opts.greyscale;

  const img = await loadImage(source);
  const { width, height } = computeTargetSize(
    img.naturalWidth || img.width,
    img.naturalHeight || img.height,
    maxEdge
  );

  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d");
  if (!ctx) {
    throw new Error("Canvas is not available in this browser.");
  }

  ctx.drawImage(img, 0, 0, width, height);

  if (greyscale) {
    const imageData = ctx.getImageData(0, 0, width, height);
    const data = imageData.data;
    for (let i = 0; i < data.length; i += 4) {
      // Luma approximation (Rec. 601)
      const y = 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2];
      data[i] = y;
      data[i + 1] = y;
      data[i + 2] = y;
    }
    ctx.putImageData(imageData, 0, 0);
  }

  const dataUrl = canvas.toDataURL("image/jpeg", quality);
  const byteLength = dataUrlByteLength(dataUrl);

  return { dataUrl, byteLength, width, height };
}

function imageHasAlpha(imageData) {
  const data = imageData.data;
  for (let i = 3; i < data.length; i += 4) {
    if (data[i] < 250) return true;
  }
  return false;
}

function dataUrlByteLength(dataUrl) {
  const base64 = (dataUrl && dataUrl.split(",")[1]) || "";
  return Math.floor((base64.length * 3) / 4);
}

const DEFAULT_LOGO_MAX_EDGE = 800;
const DEFAULT_LOGO_QUALITY = 0.85;
const PNG_BYTE_BUDGET = 400 * 1024;

/**
 * Resize a shop logo on the client. Keeps PNG when the source has
 * transparency; otherwise JPEG. Oversized PNGs fall back to JPEG.
 * @param {string} source
 * @param {object} [opts]
 * @param {number} [opts.maxEdge=800]
 * @param {number} [opts.quality=0.85]
 */
export async function processShopLogoImage(source, opts = {}) {
  if (!source) {
    throw new Error("No image to process.");
  }

  const maxEdge = opts.maxEdge || DEFAULT_LOGO_MAX_EDGE;
  const quality =
    typeof opts.quality === "number" ? opts.quality : DEFAULT_LOGO_QUALITY;

  const img = await loadImage(source);
  const { width, height } = computeTargetSize(
    img.naturalWidth || img.width,
    img.naturalHeight || img.height,
    maxEdge
  );

  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d");
  if (!ctx) {
    throw new Error("Canvas is not available in this browser.");
  }

  ctx.drawImage(img, 0, 0, width, height);
  const imageData = ctx.getImageData(0, 0, width, height);
  const transparent = imageHasAlpha(imageData);

  let dataUrl = transparent
    ? canvas.toDataURL("image/png")
    : canvas.toDataURL("image/jpeg", quality);
  let byteLength = dataUrlByteLength(dataUrl);

  if (transparent && byteLength > PNG_BYTE_BUDGET) {
    ctx.save();
    ctx.globalCompositeOperation = "destination-over";
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, width, height);
    ctx.restore();
    dataUrl = canvas.toDataURL("image/jpeg", quality);
    byteLength = dataUrlByteLength(dataUrl);
  }

  return { dataUrl, byteLength, width, height };
}

export function formatBytes(bytes) {
  if (!bytes && bytes !== 0) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}
